/* global Office */

import { initApp } from "./taskpane";

export type Template = {
  id: number;
  title: string;
  description?: string;
  department: string;
  html: string;
  type: string;
  section: string;
};

export type TemplateIndex = {
  departments: string[];
  typesByDept: Record<string, string[]>;
  templatesByDeptTypeSection: Record<string, Record<string, Record<string, Template[]>>>;
};

export type AppState = {
  templates: Template[];
  index: TemplateIndex;
  activeDept: string;
  activeType: string;
  selectedTemplate: Template | null;
  contentEventsBound: boolean;
};

export type SaveDialogFields = {
  titleInput: HTMLInputElement | null;
  descriptionInput: HTMLInputElement | null;
  departmentInput: HTMLInputElement | null;
  sectionInput: HTMLInputElement | null;
  typeInput: HTMLInputElement | null;
};

export const apiBaseUrl = "https://api.zpv-server.remotewire.net";

export const state: AppState = {
  templates: [],
  index: {
    departments: [],
    typesByDept: {},
    templatesByDeptTypeSection: {}
  },
  activeDept: "",
  activeType: "",
  selectedTemplate: null,
  contentEventsBound: false
};



export async function requestJson<T>(path: string, init: RequestInit = {}): Promise<T> {
  const email = Office.context.mailbox.userProfile.emailAddress;

  const res = await fetch(`${apiBaseUrl}${path}`, {
    ...init,
    headers: {
      ...(init.headers || {}),
      "Content-Type": "application/json",
      "x-user": email
    }
  });

  if(res.status === 403) {
    const data = await res.json();
    if(data.error === "Email verification required") {
       await requestVerification(email);
       return null as T;
    }
  }

  if (!res.ok) {
    throw new Error(`Request failed: ${res.status}`);
  }

  return res.json() as Promise<T>;
}



async function requestVerification(email: string) {
  const res = await fetch(
    `${apiBaseUrl}/auth/request-code`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify({
        email
      })
    }
  );


  if(!res.ok){
    throw new Error(
      "Could not send verification code"
    );
  }


  const data = await res.json();

  showVerificationBox(
    email,
    data.reused === true
  );
}

function showVerificationBox(email:string,reused = false){

  const overlay =
    document.getElementById(
      "placeholder-overlay"
    );


  if(!overlay) return;


  setOverlayHtml(
    overlay,
      `
  <div class="verify-box">
    <h3>
      Verify your email
    </h3>

    <p>
      We sent a code to ${escapeHtml(email)}
    </p>

    <input
      id="verifyCode"
      placeholder="123456"
      maxlength="6"
    />

    <div class="ph-actions">
      <button id="verifyBtn">
        Verify
      </button>
    </div>

    <p id="verifyMessage"></p>
  </div>
  `
  );



  document
    .getElementById("verifyBtn")
    ?.addEventListener(
      "click",
      async()=>{


        const code =
          (
            document.getElementById(
              "verifyCode"
            ) as HTMLInputElement
          )?.value;



        const res =
          await fetch(
            `${apiBaseUrl}/auth/verify-code`,
            {
              method:"POST",
              headers:{
                "Content-Type":
                  "application/json"
              },
              body:JSON.stringify({
                email,
                code
              })
            }
          );



        if(res.ok){

          hideOverlay(
            "placeholder-overlay"
          );


          await initApp();
        }
        else{

          const msg =
            document.getElementById(
              "verifyMessage"
            );

          if(msg){
            msg.textContent =
              "Invalid code";
          }

        }

      }
    );

}

export async function requestVoid(path: string, init: RequestInit = {}) {
  const email = Office.context.mailbox.userProfile.emailAddress;

  const res = await fetch(`${apiBaseUrl}${path}`, {
    ...init,
    headers: {
      ...(init.headers || {}),
      "x-user": email
    }
  });

  if (!res.ok) {
    throw new Error(`Request failed: ${res.status}`);
  }
}

export async function loadTemplates(): Promise<boolean> {

    const templates =
        await requestJson<Template[] | null>("/templates");

    if (!templates) {
        return false;
    }

    state.templates = templates;
    state.index = buildTemplateIndex(templates);

    return true;
}

export async function deleteTemplate(id: number) {
  await requestVoid(`/templates/${id}`, { method: "DELETE" });
  await loadTemplates();
}

export async function editTemplate(id: number, html: string) {
  await requestVoid(`/templates/${id}`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json"
    },
    body: JSON.stringify({ html })
  });

  await loadTemplates();
}

export async function createTemplate(payload: {
  title: string;
  description?: string;
  department: string;
  type: string;
  section: string;
  html: string;
}) {
  await requestVoid("/templates", {
    method: "POST",
    headers: {
      "Content-Type": "application/json"
    },
    body: JSON.stringify(payload)
  });
}

export function buildTemplateIndex(sourceTemplates: Template[]): TemplateIndex {
  const deptSet = new Set<string>();
  const nextTypesByDept: Record<string, Set<string>> = {};
  const nextGrouped: Record<string, Record<string, Record<string, Template[]>>> = {};

  for (const tpl of sourceTemplates) {
    deptSet.add(tpl.department);

    if (!nextTypesByDept[tpl.department]) {
      nextTypesByDept[tpl.department] = new Set();
    }
    nextTypesByDept[tpl.department].add(tpl.type);

    if (!nextGrouped[tpl.department]) {
      nextGrouped[tpl.department] = {};
    }
    if (!nextGrouped[tpl.department][tpl.type]) {
      nextGrouped[tpl.department][tpl.type] = {};
    }
    if (!nextGrouped[tpl.department][tpl.type][tpl.section]) {
      nextGrouped[tpl.department][tpl.type][tpl.section] = [];
    }

    nextGrouped[tpl.department][tpl.type][tpl.section].push(tpl);
  }

  const typesByDept: Record<string, string[]> = {};
  for (const [dept, typeSet] of Object.entries(nextTypesByDept)) {
    typesByDept[dept] = [...typeSet].sort();
  }

  return {
    departments: [...deptSet].sort(),
    typesByDept,
    templatesByDeptTypeSection: nextGrouped
  };
}

export function getTypesForDept(dept: string) {
  return state.index.typesByDept[dept] ?? [];
}

export function findTemplateById(id: number) {
  return state.templates.find(tpl => tpl.id === id) ?? null;
}

export function insert(html: string) {
  const item = Office?.context?.mailbox?.item;
  if (!item) return;

  item.body.setSelectedDataAsync(html, {
    coercionType: Office.CoercionType.Html
  });
}

export function extractPlaceholders(html: string): string[] {
  const matches = [...html.matchAll(/{{\s*([^}]+)\s*}}/g)];
  return [...new Set(matches.map(m => m[1].trim()))];
}

export function getInputType(key: string): string {
  const normalized = key.toLowerCase();

  if (normalized === "datum" || normalized === "start_datum" || normalized === "end_datum") {
    return "date";
  }

  if (normalized === "zeit" || normalized === "start_zeit" || normalized === "end_zeit") {
    return "time";
  }

  return "text";
}

export function applyPlaceholders(html: string, values: Record<string, string>) {
  return html.replace(/{{\s*(.*?)\s*}}/g, (_, key) => {
    const normalized = key.trim();
    return values[normalized] ?? "";
  });
}

export function safeDomId(value: string) {
  return value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9_-]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

export function escapeHtml(value: string) {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

export function capitalize(text: string) {
  return text.charAt(0).toUpperCase() + text.slice(1);
}

export function setOverlayHtml(overlay: HTMLElement, html: string) {
  overlay.classList.remove("hidden");
  overlay.innerHTML = html;
}

export function hideOverlay(id: string) {
  const overlay = document.getElementById(id);
  if (!overlay) return;

  overlay.classList.add("hidden");
  overlay.innerHTML = "";
}

export function showMessage(message: string) {
  const overlay = document.getElementById("placeholder-overlay");
  if (!overlay) return;

  setOverlayHtml(
    overlay,
    `
      <div class="ph-box">
        <p style="margin-bottom:12px;">${escapeHtml(message)}</p>
        <div class="ph-actions">
          <button id="msgClose" type="button">OK</button>
        </div>
      </div>
    `
  );

  document.getElementById("msgClose")?.addEventListener("click", () => {
    hideOverlay("placeholder-overlay");
  });
}

export function getSaveDialogFields(): SaveDialogFields {
  return {
    titleInput: document.getElementById("saveTitle") as HTMLInputElement | null,
    descriptionInput: document.getElementById("saveDescription") as HTMLInputElement | null,
    departmentInput: document.getElementById("saveDepartment") as HTMLInputElement | null,
    sectionInput: document.getElementById("saveSection") as HTMLInputElement | null,
    typeInput: document.getElementById("saveType") as HTMLInputElement | null
  };
}

export function readTrimmedValue(input: HTMLInputElement | null) {
  return input?.value?.trim() ?? "";
}

export function resetInputs(...inputs: Array<HTMLInputElement | null>) {
  for (const input of inputs) {
    if (input) input.value = "";
  }
}
